import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import LoginPage from "./pages/LoginPage";
import Layout from "./components/Layout";
import AdminLayout from "./pages/admin/AdminLayout";
import ManagerLayout from "./pages/manager/ManagerLayout";
import { RoleProvider, useRole } from "./contexts/RoleContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { EntitlementProvider, useEntitlement } from "./contexts/EntitlementContext";
import { ManagerProvider, useManager } from "./contexts/ManagerContext";
import { ErrorBoundary, RouteErrorBoundary } from "./components/ErrorBoundary";
import { authGetSwr } from "./utils/authFetch";
import { getMemberSession } from "./utils/memberSession";
import { rememberDirectoryEmail } from "./utils/directoryIdentity";
import { isSandboxActive } from "./utils/sandboxSession";
import { SandboxBanner, SandboxMsalProvider } from "./components/SandboxShell";

const pageImports = {
  Dashboard: () => import("./pages/Dashboard"),
  LearningModule: () => import("./pages/LearningModule"),
  CustomLearningModule: () => import("./pages/CustomLearningModule"),
  LernbereichPage: () => import("./pages/LernbereichPage"),
  TestbereichPage: () => import("./pages/TestbereichPage"),
  AwarenessPage: () => import("./pages/AwarenessPage"),
  CertificateVerifyPage: () => import("./pages/CertificateVerifyPage"),
  EinstellungenPage: () => import("./pages/EinstellungenPage"),
  GaApp: () => import("./pages/GaApp"),
  AdminDashboard: () => import("./pages/admin/AdminDashboard"),
  AdminUsers: () => import("./pages/admin/AdminUsers"),
  AdminReports: () => import("./pages/admin/AdminReports"),
  AdminUserDetail: () => import("./pages/admin/AdminUserDetail"),
  AdminGroups: () => import("./pages/admin/AdminGroups"),
  AdminGroupDetail: () => import("./pages/admin/AdminGroupDetail"),
  AdminPhishing: () => import("./pages/admin/AdminPhishing"),
  AdminPhishingDetail: () => import("./pages/admin/AdminPhishingDetail"),
  AdminModulePlans: () => import("./pages/admin/AdminModulePlans"),
  AdminCustomModules: () => import("./pages/admin/AdminCustomModules"),
  AdminMitarbeiter: () => import("./pages/admin/AdminMitarbeiter"),
  AdminMitarbeiternummern: () => import("./pages/admin/AdminMitarbeiternummern"),
  AdminEinstellungenPage: () => import("./pages/admin/AdminEinstellungenPage"),
  AdminBenachrichtigungenPage: () => import("./pages/admin/AdminBenachrichtigungenPage"),
  ManagerTeamPage: () => import("./pages/ManagerTeamPage"),
  ManagerReportsPage: () => import("./pages/manager/ManagerReportsPage"),
};

const Dashboard = lazy(pageImports.Dashboard);
const LearningModule = lazy(pageImports.LearningModule);
const CustomLearningModule = lazy(pageImports.CustomLearningModule);
const LernbereichPage = lazy(pageImports.LernbereichPage);
const TestbereichPage = lazy(pageImports.TestbereichPage);
const AwarenessPage = lazy(pageImports.AwarenessPage);
const CertificateVerifyPage = lazy(pageImports.CertificateVerifyPage);
const DemoPage = lazy(() => import("./pages/DemoPage"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const EinstellungenPage = lazy(pageImports.EinstellungenPage);
const GaApp = lazy(pageImports.GaApp);
const AdminDashboard = lazy(pageImports.AdminDashboard);
const AdminUsers = lazy(pageImports.AdminUsers);
const AdminReports = lazy(pageImports.AdminReports);
const AdminUserDetail = lazy(pageImports.AdminUserDetail);
const AdminGroups = lazy(pageImports.AdminGroups);
const AdminGroupDetail = lazy(pageImports.AdminGroupDetail);
const AdminPhishing = lazy(pageImports.AdminPhishing);
const AdminPhishingDetail = lazy(pageImports.AdminPhishingDetail);
const AdminModulePlans = lazy(pageImports.AdminModulePlans);
const AdminCustomModules = lazy(pageImports.AdminCustomModules);
const AdminMitarbeiter = lazy(pageImports.AdminMitarbeiter);
const AdminMitarbeiternummern = lazy(pageImports.AdminMitarbeiternummern);
const AdminEinstellungenPage = lazy(pageImports.AdminEinstellungenPage);
const AdminBenachrichtigungenPage = lazy(pageImports.AdminBenachrichtigungenPage);
// Bewusst nicht in `pageImports`: Das iteriert preloadAllRoutes(), und der
// Entwurf ginge sonst im Hintergrund an jeden Kunden – sichtbar ist er nur
// fuer den Administrator in der Sandbox (Route unten).
const AdminConceptPage = lazy(() => import("./pages/AdminConceptPage"));
const ReworkPage = lazy(() => import("./pages/ReworkPage"));
const ManagerTeamPage = lazy(pageImports.ManagerTeamPage);
const ManagerReportsPage = lazy(pageImports.ManagerReportsPage);

type IdleCallback = (cb: () => void) => void;
const scheduleIdle: IdleCallback = (cb) => {
  const w = window as Window & { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(cb, { timeout: 4000 });
  } else {
    window.setTimeout(cb, 1500);
  }
};

let preloadStarted = false;
function preloadAllRoutes() {
  if (preloadStarted) return;
  preloadStarted = true;
  const loaders = Object.values(pageImports);
  let index = 0;
  const step = () => {
    if (index >= loaders.length) return;
    const next = loaders[index++];
    next().catch(() => { /* prefetch is best-effort */ });
    scheduleIdle(step);
  };
  scheduleIdle(step);
}

type AdminFeature = "phishing_campaigns" | "advanced_reports" | "custom_modules" | "notifications";

const isAdminSubdomain = (() => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h.startsWith("admin.");
})();

const isGaSubdomain = (() => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h.startsWith("ga.");
})();

// Nutzt die #app-splash-Styles aus index.html, damit der HTML-Splash beim
// MSAL-Startup (handleRedirectPromise) nahtlos weiterläuft statt kurz auf
// eine leere Seite zu wechseln.
function BootSplash() {
  return (
    <div id="app-splash">
      <div className="splash-plate">
        <img src="/brand/aware-icon.png" alt="SHERLOX AWARE" width={64} height={64} />
      </div>
      <div className="splash-bar"><span /></div>
      <p className="splash-text">Security Awareness</p>
    </div>
  );
}

function RouteLoading() {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
        <div style={{ width: "32px", height: "32px", border: "3px solid var(--border)", borderTop: "3px solid #1B6AB1", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <span>Lädt…</span>
      </div>
    </div>
  );
}

function NoAccessPage({ message }: { message: string }) {
  const { instance } = useMsal();
  return (
    <div style={{
      minHeight: "100vh", background: "var(--bg-page)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: "1rem",
    }}>
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "16px",
        padding: "2.5rem", maxWidth: "400px", width: "100%",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)", textAlign: "center",
      }}>
        <div style={{
          width: "56px", height: "56px", background: "var(--danger-bg)", borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem",
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="var(--danger)" width="28" height="28">
            <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" />
          </svg>
        </div>
        <h1 style={{ fontSize: "1.25rem", fontWeight: "700", color: "var(--text-primary)", margin: "0 0 0.5rem 0" }}>
          Kein Zugriff
        </h1>
        <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", margin: "0 0 1.75rem 0", lineHeight: 1.6 }}>
          {message}
        </p>
        <button
          onClick={() => instance.logoutRedirect()}
          style={{
            width: "100%", padding: "0.75rem", background: "var(--danger)", color: "#ffffff",
            border: "none", borderRadius: "8px", fontSize: "0.875rem", fontWeight: "600",
            cursor: "pointer",
          }}
        >
          Abmelden
        </button>
      </div>
    </div>
  );
}

function RequireAdminFeature({ children, feature }: { children: JSX.Element; feature: AdminFeature }) {
  const { hasFeature, isLoading } = useEntitlement();
  if (isLoading) return children;
  if (!hasFeature(feature)) return <Navigate to="/admin" replace />;
  return children;
}

// Content-Admin ohne Modulbaukasten im Paket: nicht nach /admin umleiten (das
// führt beim Content-Admin wieder hierher), sondern auf das Ziel `fallback`.
function RequireContentEditing({ children, fallback }: { children: JSX.Element; fallback: JSX.Element }) {
  const { hasFeature, isLoading } = useEntitlement();
  if (isLoading) return children;
  if (!hasFeature("custom_modules")) return fallback;
  return children;
}

// Manager-Bereich nur für Mitarbeiter mit mindestens einem direct report.
function RequireManager({ children }: { children: JSX.Element }) {
  const { isManager, isLoading } = useManager();
  if (isLoading) return <RouteLoading />;
  if (!isManager) return <Navigate to="/dashboard" replace />;
  return children;
}

// Testbereich nur, wenn der Tenant den großen Sicherheitstest nicht deaktiviert hat.
function RequireTestEnabled({ children }: { children: JSX.Element }) {
  const { entitlement, isLoading } = useEntitlement();
  if (!isLoading && entitlement && entitlement.features.test_enabled === false) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function formatDateDe(value?: string): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function LicenseLockScreen({ status, validUntil, accountDeactivated = false }: { status?: string; validUntil?: string; accountDeactivated?: boolean }) {
  const { instance } = useMsal();
  const { language } = useLanguage();
  const deactivated = status === "suspended" || status === "inactive";
  const dateStr = formatDateDe(validUntil);
  // Eigenes Konto vom Admin des Unternehmens deaktiviert (nicht der ganze Mandant).
  const message = accountDeactivated
    ? (language === "en"
      ? "Your SHERLOX AWARE account has been deactivated. Please contact your administrator if you need access again."
      : "Ihr Konto in SHERLOX AWARE wurde deaktiviert. Wenden Sie sich bitte an Ihre Administration, falls Sie wieder Zugang benötigen.")
    : deactivated
    ? "Dieser Zugang wurde deaktiviert. Bitte kontaktieren Sie Ihren Anbieter, um ihn zu reaktivieren."
    : `Die Lizenz für diesen Zugang ist${dateStr ? ` am ${dateStr}` : ""} abgelaufen. Bitte kontaktieren Sie Ihren Anbieter, um den Zugang zu verlängern.`;
  const title = accountDeactivated && language === "en" ? "Account deactivated" : accountDeactivated ? "Konto deaktiviert" : "Zugang gesperrt";
  const logoutLabel = accountDeactivated && language === "en" ? "Sign out" : "Abmelden";
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "16px", padding: "2.5rem", maxWidth: "440px", width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", textAlign: "center" }}>
        <div style={{ width: "56px", height: "56px", background: "var(--danger-bg)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem" }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="var(--danger)" width="28" height="28">
            <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" />
          </svg>
        </div>
        <h1 style={{ fontSize: "1.25rem", fontWeight: "700", color: "var(--text-primary)", margin: "0 0 0.5rem 0" }}>
          {title}
        </h1>
        <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", margin: "0 0 1.75rem 0", lineHeight: 1.6 }}>
          {message}
        </p>
        <button
          onClick={() => instance.logoutRedirect()}
          style={{ width: "100%", padding: "0.75rem", background: "var(--danger)", color: "#ffffff", border: "none", borderRadius: "8px", fontSize: "0.875rem", fontWeight: "600", cursor: "pointer" }}
        >
          {logoutLabel}
        </button>
      </div>
    </div>
  );
}

function ExpiryWarningToast({ days, validUntil, tenantId }: { days: number; validUntil?: string; tenantId: string }) {
  const storageKey = `sherloxaware_expiry_warn_dismissed_${tenantId}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return sessionStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  const dateStr = formatDateDe(validUntil);
  const dismiss = () => {
    try { sessionStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };
  return (
    <div style={{ position: "fixed", bottom: "1rem", right: "1rem", zIndex: 99999, maxWidth: "360px", background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: "12px", padding: "0.9rem 1rem", boxShadow: "0 8px 24px rgba(0,0,0,0.18)", display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
      <span aria-hidden style={{ fontSize: "1.1rem", lineHeight: 1.2 }}>⚠️</span>
      <div style={{ flex: 1, fontSize: "0.82rem", color: "#92400E", lineHeight: 1.45 }}>
        <strong>Lizenz läuft bald ab.</strong> Noch {days} {days === 1 ? "Werktag" : "Werktage"}{dateStr ? ` (bis ${dateStr})` : ""}. Bitte rechtzeitig verlängern, sonst wird der Zugang gesperrt.
      </div>
      <button
        onClick={dismiss}
        aria-label="Hinweis schließen"
        style={{ background: "transparent", border: "none", color: "#92400E", fontSize: "1.1rem", lineHeight: 1, cursor: "pointer", padding: 0, flexShrink: 0 }}
      >
        ×
      </button>
    </div>
  );
}

function TenantGate({ children }: { children: ReactNode }) {
  const { isLocked, isLoading, entitlement, expiryBusinessDays } = useEntitlement();
  const { tenantId } = useRole();
  if (isLoading) return <RouteLoading />;
  if (entitlement?.accountDeactivated) return <LicenseLockScreen accountDeactivated />;
  if (isLocked) return <LicenseLockScreen status={entitlement?.status} validUntil={entitlement?.validUntil} />;
  return (
    <>
      {children}
      {expiryBusinessDays !== null && expiryBusinessDays > 0 && expiryBusinessDays <= 5 && (
        <ExpiryWarningToast days={expiryBusinessDays} validUntil={entitlement?.validUntil} tenantId={tenantId} />
      )}
    </>
  );
}

const LAST_PATH_KEY = "sherloxaware_last_path";

// Merkt sich die zuletzt erfolgreich gerenderte Route. Ein Reload oder ein
// MSAL-Redirect-Roundtrip (z.B. wenn eine stille Token-Erneuerung fehlschlägt
// und acquireTokenRedirect nötig wird) landet manchmal auf der nackten Origin
// statt auf der ursprünglichen URL — RootRedirect greift dann auf diesen Wert
// zurück statt immer /dashboard bzw. /admin zu zeigen.
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === "/") return;
    try {
      sessionStorage.setItem(LAST_PATH_KEY, location.pathname + location.search);
    } catch {
      // ignore
    }
  }, [location.pathname, location.search]);
  return null;
}

function RootRedirect({ fallback }: { fallback: string }) {
  let lastPath: string | null = null;
  try {
    lastPath = sessionStorage.getItem(LAST_PATH_KEY);
  } catch {
    lastPath = null;
  }
  const target = lastPath && lastPath !== "/" ? lastPath : fallback;
  return <Navigate to={target} replace />;
}

const SharedMemberLogin = lazy(() => import("./pages/SharedMemberLogin"));

/**
 * Sammelaccounts: Ein Microsoft-Konto, viele Mitarbeiter ohne eigenes Postfach.
 * Ist das angemeldete Konto als Sammelaccount markiert (Admin-Bereich →
 * Mitarbeiter), kommt vor dem Mitarbeiterbereich die Anmeldung mit
 * Mitarbeiternummer. Ohne diese Anmeldung gibt es keine Inhalte —
 * sonst liefe der Fortschritt auf das gemeinsame Konto statt auf die Person.
 */
// Merker pro Konto: Ohne ihn müsste beim Start jeder einzelne Nutzer auf die
// Status-Antwort warten, bevor der Mitarbeiterbereich überhaupt erscheint.
// Mit ihm rendert der Normalfall sofort und die Antwort wird im Hintergrund
// geprüft – ändert sie sich, lädt die Seite einmal neu.
const SHARED_HINT_PREFIX = "sherloxaware_shared_hint_v1:";

function readSharedHint(email: string): boolean | null {
  if (!email) return null;
  try {
    const raw = localStorage.getItem(SHARED_HINT_PREFIX + email);
    return raw === "1" ? true : raw === "0" ? false : null;
  } catch {
    return null;
  }
}

function writeSharedHint(email: string, shared: boolean): void {
  try { localStorage.setItem(SHARED_HINT_PREFIX + email, shared ? "1" : "0"); } catch { /* ignore */ }
}

/**
 * Sammelaccounts: Ein Microsoft-Konto, viele Mitarbeiter ohne eigenes Postfach.
 * Ist das angemeldete Konto als Sammelaccount markiert (Admin-Bereich →
 * Mitarbeiter → „Sammelaccount"), kommt vor dem Mitarbeiterbereich die Anmeldung
 * mit Mitarbeiternummer. Ohne diese Anmeldung gibt es keine Inhalte —
 * sonst liefe der Fortschritt auf das gemeinsame Konto statt auf die Person.
 */
function SharedAccountGate({ children }: { children: ReactNode }) {
  const { tenantId } = useRole();
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];
  const accountEmail = (account?.username ?? "").toLowerCase();
  // Nur ein positives Cache-Ergebnis ist sicher verwendbar. Ein gecachtes
  // `false` könnte manipuliert oder seit dem Einschalten des Sammelaccounts
  // veraltet sein und darf die zusätzliche Anmeldung nie überspringen.
  const [shared, setShared] = useState<boolean | null>(() => readSharedHint(accountEmail) === true ? true : null);
  const [label, setLabel] = useState("");
  const [statusTimedOut, setStatusTimedOut] = useState(false);

  useEffect(() => {
    if (!tenantId || !accountEmail) return;
    let cancelled = false;
    // `for` wertet der Server nicht aus (er nimmt das Konto aus dem Token) – es
    // steht hier, weil der GET-Cache in authFetch über die URL geht: ohne das
    // Konto im Schlüssel bekäme der nächste Anmelder am selben Gerät die Antwort
    // für den vorherigen und damit womöglich gar keine Anmeldemaske.
    // authGetSwr statt authFetch: der Status kommt sofort aus dem Cache des
    // letzten Starts (er blockiert sonst den Splash bis eine kalte Function
    // antwortet) und die frische Antwort wird nachgereicht, sobald sie da ist.
    const cancelSwr = authGetSwr(
      `/api/user-profiles?tenantId=${encodeURIComponent(tenantId)}&shared=status&for=${encodeURIComponent(accountEmail)}`,
      (data, fresh) => {
        if (cancelled) return;
        const obj = (data && typeof data === "object" ? data : {}) as { sharedAccount?: unknown; label?: unknown };
        const isShared = obj.sharedAccount === true;
        // Ein negatives localStorage-Ergebnis ist keine Sicherheitsentscheidung.
        // Erst die frisch authentifizierte Serverantwort darf Inhalte freigeben.
        if (!fresh && !isShared) return;
        // Führt das Verzeichnis das Konto unter einer anderen Adresse als dem
        // Anmeldenamen, einmal neu laden: alles darunter liest die eigene
        // Adresse beim Rendern und soll nicht halb mit der alten weiterlaufen.
        if (fresh && rememberDirectoryEmail(accountEmail, (obj as { directoryEmail?: unknown }).directoryEmail)) {
          window.location.reload();
          return;
        }
        const previous = readSharedHint(accountEmail);
        writeSharedHint(accountEmail, isShared);
        setLabel(typeof obj.label === "string" ? obj.label : "");
        setStatusTimedOut(false);
        setShared(isShared);
        // Der Merker lag daneben (Konto wurde gerade um- oder abgestellt): einmal
        // neu laden, damit niemand mit der falschen Identität weiterarbeitet.
        if (previous !== null && previous !== isShared) window.location.reload();
      },
    );
    // Antwortet der Status-Endpunkt nicht, im Zweifel geschlossen bleiben. Die
    // API erzwingt dieselbe Grenze zusätzlich serverseitig.
    const giveUp = window.setTimeout(() => {
      if (!cancelled) setStatusTimedOut(true);
    }, 8000);
    return () => { cancelled = true; cancelSwr(); window.clearTimeout(giveUp); };
  }, [tenantId, accountEmail]);

  if (!tenantId || shared === null) {
    if (statusTimedOut) {
      return <NoAccessPage message="Der Kontostatus konnte nicht sicher geprüft werden. Bitte lade die Seite neu oder melde dich erneut an." />;
    }
    return <BootSplash />;
  }
  if (shared && !getMemberSession()) {
    return (
      <Suspense fallback={<RouteLoading />}>
        <SharedMemberLogin tenantId={tenantId} accountLabel={label} />
      </Suspense>
    );
  }
  return <>{children}</>;
}

function MainRoutes() {
  const { isAdmin, isContentAdmin, tenantId, isLoading } = useRole();
  useEffect(() => { preloadAllRoutes(); }, []);
  // Admin status is resolved async (Azure claims refresh + DB allowlist fetch); rendering
  // the routes before both settle would register only the employee routes and immediately
  // bounce an admin to /dashboard via the catch-all before isAdmin flips to true.
  if (isLoading) return <RouteLoading />;
  return (
    <EntitlementProvider tenantId={tenantId}>
      <ManagerProvider>
      <LanguageProvider>
        <TenantGate>
        <BrowserRouter>
          <RouteTracker />
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              <Route path="/" element={<RootRedirect fallback="/dashboard" />} />
              {isAdmin && isSandboxActive() && (
                <Route path="/admin-konzept" element={<AdminConceptPage />} />
              )}
              {isSandboxActive() && <Route path="/rework" element={<ReworkPage />} />}
              <Route element={<Layout hideAdminLink={!isAdmin && !isContentAdmin}><RouteErrorBoundary><Outlet /></RouteErrorBoundary></Layout>}>
                <Route path="/dashboard" element={<Dashboard />} />
                {/* Die alte Quizseite ist entfernt; Lesezeichen landen im Lernbereich. */}
                <Route path="/training" element={<Navigate to="/lernmodule" replace />} />
                <Route path="/training/:moduleId" element={<LearningModule />} />
                <Route path="/training/custom/:moduleId" element={<CustomLearningModule />} />
                <Route path="/lernmodule" element={<LernbereichPage />} />
                <Route path="/test" element={<RequireTestEnabled><TestbereichPage /></RequireTestEnabled>} />
                <Route path="/mein-team" element={<Navigate to="/manager" replace />} />
                <Route path="/einstellungen" element={<EinstellungenPage />} />
              </Route>
              <Route element={<RequireManager><ManagerLayout><RouteErrorBoundary><Outlet /></RouteErrorBoundary></ManagerLayout></RequireManager>}>
                <Route path="/manager" element={<ManagerTeamPage />} />
                <Route path="/manager/team" element={<Navigate to="/manager" replace />} />
                <Route path="/manager/berichte" element={<ManagerReportsPage />} />
                <Route path="/manager/fortschritt" element={<Navigate to="/manager" replace />} />
              </Route>
              {isAdmin && (
                <Route element={<AdminLayout><RouteErrorBoundary><Outlet /></RouteErrorBoundary></AdminLayout>}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/mitarbeiter" element={<AdminUsers />} />
                  <Route path="/admin/mitarbeiter/:userId" element={<AdminUserDetail />} />
                  <Route path="/admin/mitarbeiternummern" element={<AdminMitarbeiternummern />} />
                  <Route path="/admin/gruppen" element={<AdminGroups />} />
                  <Route path="/admin/gruppen/:groupName" element={<AdminGroupDetail />} />
                  <Route path="/admin/phishing" element={<RequireAdminFeature feature="phishing_campaigns"><AdminPhishing /></RequireAdminFeature>} />
                  <Route path="/admin/phishing/:campaignId" element={<RequireAdminFeature feature="phishing_campaigns"><AdminPhishingDetail /></RequireAdminFeature>} />
                  <Route path="/admin/training" element={<Navigate to="/admin/mitarbeiter" replace />} />
                  <Route path="/admin/module-plaene" element={<AdminModulePlans />} />
                  <Route path="/admin/eigene-module" element={<RequireAdminFeature feature="custom_modules"><AdminCustomModules /></RequireAdminFeature>} />
                  <Route path="/admin/risiko" element={<AdminMitarbeiter />} />
                  <Route path="/admin/reports" element={<RequireAdminFeature feature="advanced_reports"><AdminReports /></RequireAdminFeature>} />
                  <Route path="/admin/einstellungen" element={<AdminEinstellungenPage />} />
                  <Route path="/admin/benachrichtigungen" element={<RequireAdminFeature feature="notifications"><AdminBenachrichtigungenPage /></RequireAdminFeature>} />
                </Route>
              )}
              {/* Content-Admin: vom Admin-Bereich nur die Modulbearbeitung. */}
              {isContentAdmin && (
                <Route element={<AdminLayout><RouteErrorBoundary><Outlet /></RouteErrorBoundary></AdminLayout>}>
                  <Route path="/admin" element={<Navigate to="/admin/eigene-module" replace />} />
                  <Route path="/admin/eigene-module" element={<RequireContentEditing fallback={<Navigate to="/dashboard" replace />}><AdminCustomModules /></RequireContentEditing>} />
                  <Route path="/admin/*" element={<Navigate to="/admin/eigene-module" replace />} />
                </Route>
              )}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </TenantGate>
      </LanguageProvider>
      </ManagerProvider>
    </EntitlementProvider>
  );
}

function TenantAdminRoutes() {
  const { isAdmin, isContentAdmin, tenantId, isLoading } = useRole();
  useEffect(() => { preloadAllRoutes(); }, []);

  if (isLoading) return <RouteLoading />;

  if (isContentAdmin) {
    return (
      <EntitlementProvider tenantId={tenantId}>
        <TenantGate>
        <BrowserRouter>
          <RouteTracker />
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              <Route element={<AdminLayout><Outlet /></AdminLayout>}>
                <Route path="/admin/eigene-module" element={<RequireContentEditing fallback={<NoAccessPage message="Die Modulbearbeitung ist für euer Paket nicht freigeschaltet." />}><AdminCustomModules /></RequireContentEditing>} />
              </Route>
              <Route path="*" element={<Navigate to="/admin/eigene-module" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </TenantGate>
      </EntitlementProvider>
    );
  }

  if (!isAdmin) {
    return (
      <NoAccessPage message="Dieses Portal ist ausschließlich für autorisierte Unternehmensadministratoren. Dein Account hat keine Admin-Rechte." />
    );
  }

  return (
    <EntitlementProvider tenantId={tenantId}>
      <TenantGate>
      <BrowserRouter>
        <RouteTracker />
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            <Route path="/" element={<RootRedirect fallback="/admin" />} />
            <Route element={<AdminLayout><Outlet /></AdminLayout>}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/mitarbeiter" element={<AdminUsers />} />
              <Route path="/admin/mitarbeiter/:userId" element={<AdminUserDetail />} />
              <Route path="/admin/mitarbeiternummern" element={<AdminMitarbeiternummern />} />
              <Route path="/admin/gruppen" element={<AdminGroups />} />
              <Route path="/admin/gruppen/:groupName" element={<AdminGroupDetail />} />
              <Route path="/admin/phishing" element={<RequireAdminFeature feature="phishing_campaigns"><AdminPhishing /></RequireAdminFeature>} />
              <Route path="/admin/phishing/:campaignId" element={<RequireAdminFeature feature="phishing_campaigns"><AdminPhishingDetail /></RequireAdminFeature>} />
              <Route path="/admin/training" element={<Navigate to="/admin/mitarbeiter" replace />} />
              <Route path="/admin/risiko" element={<AdminMitarbeiter />} />
              <Route path="/admin/reports" element={<RequireAdminFeature feature="advanced_reports"><AdminReports /></RequireAdminFeature>} />
              <Route path="/admin/einstellungen" element={<AdminEinstellungenPage />} />
              <Route path="/admin/benachrichtigungen" element={<RequireAdminFeature feature="notifications"><AdminBenachrichtigungenPage /></RequireAdminFeature>} />
            </Route>
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </TenantGate>
    </EntitlementProvider>
  );
}

function AppContent() {
  const { inProgress } = useMsal();
  const msalBooting = inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect;

  // Landing-Chunk sofort vorladen (parallel zum MSAL-Boot), statt erst nach
  // Auth-Abschluss über den Suspense-Fallback — spart einen Round-Trip.
  useEffect(() => {
    if (isGaSubdomain) {
      pageImports.GaApp().catch(() => {});
      return;
    }
    pageImports.Dashboard().catch(() => {});
    pageImports.AdminDashboard().catch(() => {});
  }, []);

  const isAwarenessRoute = (() => {
    if (typeof window === "undefined") return false;
    return window.location.pathname.replace(/\/+$/, "") === "/awareness";
  })();

  const isCertificateVerifyRoute = (() => {
    if (typeof window === "undefined") return false;
    // "/zertifikat" (Prüfcode-Eingabe bzw. ?code=) und "/zertifikat/<id>" (ältere QR-Codes)
    const path = window.location.pathname.replace(/\/+$/, "");
    return path === "/zertifikat" || path.startsWith("/zertifikat/");
  })();

  const isDemoRoute = (() => {
    if (typeof window === "undefined") return false;
    return window.location.pathname.replace(/\/+$/, "") === "/demo";
  })();

  // Anbieterkennzeichnung (§ 5 DDG) muss ohne Login erreichbar sein – daher vor
  // jeder Auth-Weiche und auch auf der GA-Subdomain.
  const isImpressumRoute = (() => {
    if (typeof window === "undefined") return false;
    return window.location.pathname.replace(/\/+$/, "") === "/impressum";
  })();

  if (isImpressumRoute) return <ErrorBoundary><Suspense fallback={<RouteLoading />}><ImpressumPage /></Suspense></ErrorBoundary>;
  if (isDemoRoute) return <ErrorBoundary><Suspense fallback={<RouteLoading />}><DemoPage /></Suspense></ErrorBoundary>;
  if (isAwarenessRoute) return <ErrorBoundary><Suspense fallback={<RouteLoading />}><AwarenessPage /></Suspense></ErrorBoundary>;
  if (isCertificateVerifyRoute) return <ErrorBoundary><Suspense fallback={<RouteLoading />}><CertificateVerifyPage /></Suspense></ErrorBoundary>;

  if (isGaSubdomain) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<RouteLoading />}>
          <GaApp />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Sandbox-Sitzung: kein Microsoft-Login, eigener Token-Weg. Bewusst vor der
  // MSAL-Weiche – die Sandbox soll weder auf handleRedirectPromise warten noch
  // je die Anmeldemaske sehen. Der MSAL-Kontext wird darunter durch ein
  // synthetisches Konto ersetzt (SandboxShell.tsx), damit die restliche App
  // unverändert läuft.
  if (isSandboxActive()) {
    return (
      <ErrorBoundary>
        <SandboxMsalProvider>
          <RoleProvider>
            <SharedAccountGate>
              <MainRoutes />
            </SharedAccountGate>
          </RoleProvider>
          <SandboxBanner />
        </SandboxMsalProvider>
      </ErrorBoundary>
    );
  }

  if (msalBooting) return <BootSplash />;

  if (isAdminSubdomain) {
    return (
      <>
        <UnauthenticatedTemplate><LoginPage /></UnauthenticatedTemplate>
        <AuthenticatedTemplate>
          <RoleProvider>
            <TenantAdminRoutes />
          </RoleProvider>
        </AuthenticatedTemplate>
      </>
    );
  }

  return (
    <>
      <UnauthenticatedTemplate><LoginPage /></UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <RoleProvider>
          <SharedAccountGate>
            <MainRoutes />
          </SharedAccountGate>
        </RoleProvider>
      </AuthenticatedTemplate>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
